"use client"
import { useState } from "react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { signIn } from "next-auth/react"
import { Palette, Eye, EyeOff } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { COUNTRIES } from "@/lib/countries"
import { FoundingSpotsBadge } from "@/components/FoundingSpotsBadge"

function generateChallenge() {
  const a = Math.floor(Math.random() * 9) + 1
  const b = Math.floor(Math.random() * 9) + 1
  return { question: `${a} + ${b}`, answer: a + b }
}

export default function RegisterPage() {
  const router = useRouter()
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [country, setCountry] = useState("")
  const [city, setCity] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [botField, setBotField] = useState("") // honeypot — should remain empty
  const [challenge] = useState(() => generateChallenge())
  const [challengeAnswer, setChallengeAnswer] = useState("")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!agreedToTerms) {
      setError("You must agree to the Terms of Use, Privacy Policy, and Community Guidelines.")
      return
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.")
      return
    }
    if (parseInt(challengeAnswer) !== challenge.answer) {
      setError("Incorrect answer to the verification question. Please try again.")
      return
    }
    setLoading(true)
    setError("")

    const res = await fetch("/api/register", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name, email, password,
        country: country || undefined,
        city: city || undefined,
        agreedToTerms,
        honeyput: botField || undefined, // honeypot field — bots fill this
        challengeAnswer: parseInt(challengeAnswer),
        challengeExpected: challenge.answer,
      }),
    })

    if (!res.ok) {
      const data = await res.json()
      setError(data.error ?? "Registration failed.")
      setLoading(false)
      return
    }

    // Auto sign in after registration
    await signIn("credentials", { email, password, redirect: false })
    router.push("/")
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-12 h-12 rounded-xl bg-[var(--primary)] flex items-center justify-center mx-auto mb-4">
            <Palette size={22} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold">Join RMAR</h1>
          <p className="text-sm text-[var(--muted-foreground)] mt-1">
            Create a free account to write reviews and track residencies
          </p>
          <FoundingSpotsBadge className="mt-3" />
        </div>

        <form onSubmit={handleSubmit} className="relative space-y-4">
          <div>
            <label className="text-sm font-medium block mb-1">Full Name</label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              required
              autoComplete="name"
            />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Email</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoComplete="email"
            />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Password</label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min 8 characters"
                required
                autoComplete="new-password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>

          {/* Location — optional, used for community stats */}
          <div className="pt-1 space-y-3">
            <p className="text-xs text-[var(--muted-foreground)]">Where are you based? <span className="italic">(optional — helps us understand our community)</span></p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium block mb-1">City</label>
                <Input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="e.g. Brooklyn"
                  autoComplete="address-level2"
                />
              </div>
              <div>
                <label className="text-xs font-medium block mb-1">Country</label>
                <select
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="w-full border border-[var(--border)] rounded-[var(--radius)] px-3 py-2 text-sm bg-[var(--background)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
                >
                  <option value="">— select —</option>
                  {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Honeypot field — hidden from real users, bots auto-fill it */}
          <div className="absolute -left-[9999px]" aria-hidden="true">
            <label htmlFor="website">Website</label>
            <input
              type="text"
              id="website"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={botField}
              onChange={(e) => setBotField(e.target.value)}
            />
          </div>

          {/* Human verification */}
          <div>
            <label className="text-sm font-medium block mb-1">
              Quick verification: What is {challenge.question}?
            </label>
            <Input
              type="number"
              value={challengeAnswer}
              onChange={(e) => setChallengeAnswer(e.target.value)}
              placeholder="Your answer"
              required
              autoComplete="off"
            />
          </div>

          {/* Terms agreement checkbox */}
          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              id="agree-terms"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-[var(--border)] accent-[var(--primary)]"
            />
            <label htmlFor="agree-terms" className="text-xs text-[var(--muted-foreground)] leading-snug">
              I have read and agree to the{" "}
              <Link href="/terms" className="underline hover:text-[var(--foreground)]" target="_blank">Terms of Use</Link>,{" "}
              <Link href="/privacy" className="underline hover:text-[var(--foreground)]" target="_blank">Privacy Policy</Link>, and{" "}
              <Link href="/guidelines" className="underline hover:text-[var(--foreground)]" target="_blank">Community Guidelines</Link>.
            </label>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <Button type="submit" className="w-full" disabled={loading || !agreedToTerms}>
            {loading ? "Creating account..." : "Create Free Account"}
          </Button>
        </form>

        <p className="text-center text-sm text-[var(--muted-foreground)] mt-6">
          Already have an account?{" "}
          <Link href="/login" className="text-[var(--primary)] hover:underline font-medium">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  )
}
